import { CssPropertyType, DefaultClient } from '../../types';
import { decompose } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { SpacingDataResult, SpacingPropertyData } from '../types';
import { checkForMixedValues } from '../utils';
import { VariableResolver } from '../../common/abstractStyles/variableResolver';

export const getSpacingData = async (
  clients: DefaultClient[],
  allowedStyles: CssPropertyType[],
): Promise<SpacingDataResult> => {
  const allowedStylesSet = new Set(allowedStyles);
  const propertiesMap = new Map<DefaultClient, SpacingPropertyData[]>();

  let lastCheckedValue: string | null = null;
  let isMixed = false;

  for (const client of clients) {
    const styles = await client.element.getStyles();
    const stylesDefinitions = await client.element.getStyleDefinitions();
    propertiesMap.set(client, []);
    const clientData = propertiesMap.get(client)!;

    const allVariables = VariableResolver.extractAllVariableNames(
      styles.cssProperties,
      styles.cssCustomProperties,
    );

    const themeVariables: Record<string, string> = {};

    if (allVariables.length > 0) {
      const themeCustomProperties = await client.info.getThemeCustomProperties({
        keys: allVariables.map((v) => `--${v}`),
      });

      Object.entries(themeCustomProperties).forEach(([varName, data]) => {
        if ('value' in data) {
          themeVariables[varName] = data.value;
        }
      });
    }

    // eslint-disable-next-line no-loop-func
    Object.entries(styles.cssProperties).forEach(([key, value]) => {
      if (!allowedStylesSet.has(key as CssPropertyType)) {
        return;
      }

      const resolvedValue = VariableResolver.resolveVariables(
        value,
        themeVariables,
      );
      const decomposed = decompose(key as CssPropertyType, resolvedValue);

      if (decomposed) {
        Object.values(decomposed).forEach((decomposedValue) => {
          if (!decomposedValue) {
            return;
          }

          if (checkForMixedValues(decomposedValue, lastCheckedValue)) {
            isMixed = true;
          }

          lastCheckedValue = decomposedValue;
        });
      } else {
        if (checkForMixedValues(resolvedValue, lastCheckedValue)) {
          isMixed = true;
        }

        lastCheckedValue = resolvedValue;
      }

      clientData.push({
        originalValue: resolvedValue,
        propertyType: key as CssPropertyType,
        styleItemId: key,
        namespace: 'cssProperties',
      });
    });

    // eslint-disable-next-line no-loop-func
    Object.entries(styles.cssCustomProperties).forEach(([key, value]) => {
      const definition = stylesDefinitions.cssCustomProperties[key];
      const cssPropertyType = definition?.cssPropertyType;

      if (!cssPropertyType || !allowedStylesSet.has(cssPropertyType)) {
        return;
      }

      const resolvedValue = VariableResolver.resolveVariables(
        value,
        themeVariables,
      );
      const decomposed = decompose(cssPropertyType, resolvedValue);

      if (decomposed) {
        Object.values(decomposed).forEach((decomposedValue) => {
          if (!decomposedValue) {
            return;
          }

          if (checkForMixedValues(decomposedValue, lastCheckedValue)) {
            isMixed = true;
          }

          lastCheckedValue = decomposedValue;
        });
      } else {
        if (checkForMixedValues(resolvedValue, lastCheckedValue)) {
          isMixed = true;
        }

        lastCheckedValue = resolvedValue;
      }

      clientData.push({
        originalValue: resolvedValue,
        propertyType: cssPropertyType,
        styleItemId: key,
        namespace: 'cssCustomProperties',
      });
    });
  }

  return {
    isMixed,
    sharedValue: isMixed ? null : lastCheckedValue,
    propertiesMap,
  };
};
